import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { log, warn } from '@execonline-inc/logging';
import { fromBool } from '@execonline-inc/maybe-adapter';
import { whenZero } from '@execonline-inc/numbers';
import { assertNever, pipe } from '@kofno/piper';
import {
  Buffering,
  Ended,
  Initialized,
  Kettle,
  Paused,
  Playing,
  Ready,
  VideoState,
} from '@execonline-inc/kettle.private';
import { Maybe, fromNullable, just, nothing } from 'maybeasy';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Task } from 'taskarian';
import {
  JWPlayerFn,
  JWPlayerInitializationError,
  JWPlayerObj,
  MetaEvent,
  TracksSourceFile,
  VideoSourceFile,
  thumbnailsSourceFile,
} from '../../JWPlayer/Types';
import { loadJWPlayer } from './Loader';
import { ReqHlsPlayerOptions, prepareOptions } from './Types';
import * as style from './style.module.css';

export interface Props {
  id: string;
  className: string;
  kettle: Kettle;
  options: Task<JWPlayerInitializationError, ReqHlsPlayerOptions>;
  fallbackSources?: VideoSourceFile[];
}

function JWPlayer(props: Props) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const { kettle, options, fallbackSources, id, className } = props;

  React.useEffect(() => {
    const container = containerRef.current;
    const stopReactions = Task.succeed<JWPlayerInitializationError, {}>({})
      .assign('options', options)
      .assign('player', ({ options }) => loadJWPlayer(options.playerUrl, options.playerKey))
      .fork(initializationError, ({ options, player }) => {
        initializationSuccess(finalizeOptions(options), player);
      });

    return () => {
      just({})
        .assign('jwplayer', fromNullable(window.jwplayer))
        .assign('container', fromNullable(container))
        .do(({ jwplayer, container }) => jwplayer(container).remove());

      fromNullable(stopReactions).do(() => stopReactions());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs only on mount; functions are recreated each render
  }, []);

  const registerKettleReactions = (playerObj: JWPlayerObj) => {
    return reaction(
      () => kettle.videoMessage.length,
      (_length: number) => {
        kettle.popMessage().map((msg) =>
          msg.cata({
            play: () => {
              playerObj.play();
            },
            pause: () => {
              playerObj.pause();
            },
            seekTo: (pos) => {
              playerObj.seek(pos);
            },
          }),
        );
      },
    );
  };

  const updateKettle = (
    playerObj: JWPlayerObj,
    fn: (pos: Maybe<number>, duration: Maybe<number>) => VideoState,
  ) => {
    const pos = just(playerObj.getPosition());
    const dur = just(playerObj.getDuration());
    kettle.setVideoState(fn(pos, dur));
  };

  const ready = (playerObj: JWPlayerObj) =>
    updateKettle(playerObj, (pos, dur) => new Ready(pos, dur));

  const handlePlay = (playerObj: JWPlayerObj) => () =>
    updateKettle(playerObj, (pos, dur) => new Playing(pos, dur));

  const handlePause = (playerObj: JWPlayerObj) => () =>
    updateKettle(playerObj, (pos, dur) => new Paused(pos, dur));

  const handleEnd = (playerObj: JWPlayerObj) => () =>
    updateKettle(playerObj, (pos, dur) => new Ended(pos, dur));

  const handleTime = (playerObj: JWPlayerObj) => () => {
    updateKettle(playerObj, (pos, dur) =>
      kettle.videoState.cata({
        playing: () => new Playing(pos, dur),
        paused: () => new Paused(pos, dur),
        ended: () => new Ended(pos, dur),
        buffering: () => new Buffering(pos, dur),
        ready: () => new Ready(pos, dur),
        initialized: () => new Initialized(),
      }),
    );
  };

  const handleFallback = (err: string, playerObj: JWPlayerObj, options: ReqHlsPlayerOptions) => {
    warn(`Initial setup failed for ${id}`, err);
    fromNullable(fallbackSources)
      .map((fallbackSources) => {
        warn(`Attempting fallback for ${id}`, fallbackSources);
        const refinedOptions: ReqHlsPlayerOptions = {
          ...options,
          file: nothing(),
          sources: fromNullable(fallbackSources),
        };
        setup(playerObj, refinedOptions);
      })
      .getOrElse(() =>
        warnAndNotify('Lecture video failed to load', `No fallback sources for ${id}`, props),
      );
  };

  const setAspectRatio =
    (playerObj: JWPlayerObj, options: ReqHlsPlayerOptions) => (e: MetaEvent) => {
      const newOptions = { ...options, aspectratio: just(`${e.width}:${e.height}`) };
      finalSetup(playerObj, newOptions);
    };

  const setup = (playerObj: JWPlayerObj, options: ReqHlsPlayerOptions) => {
    fromBool(options.dynamicallySetAspectRatio, options)
      .map((options) => {
        playerObj.setup(prepareOptions(options));
        playerObj.on('ready', () => {
          const stopReactions = registerKettleReactions(playerObj);
          playerObj.on('meta', setAspectRatio(playerObj, options));
          return stopReactions;
        });
      })
      .getOrElse(() => finalSetup(playerObj, options));
  };

  const finalSetup = (playerObj: JWPlayerObj, options: ReqHlsPlayerOptions) => {
    playerObj.setup(prepareOptions(options));
    playerObj.on('ready', () => {
      const stopReactions = registerKettleReactions(playerObj);
      ready(playerObj);
      playerObj.on('play', handlePlay(playerObj));
      playerObj.on('pause', handlePause(playerObj));
      playerObj.on('time', handleTime(playerObj));
      playerObj.on('seeked', handleTime(playerObj));
      playerObj.on('ended', handleEnd(playerObj));
      playerObj.on('meta', handleTime(playerObj));
      playerObj.on('complete', handleEnd(playerObj));
      return stopReactions;
    });
  };

  const initializationError = (err: JWPlayerInitializationError): void => {
    switch (err.kind) {
      case 'thumbnail-preview-missing-error':
        warnAndNotify('ThumbnailPreviewMissingError', ` ${err.key}`, err);
        break;
      case 'sprite-missing-error':
        warnAndNotify('SpriteMissingError', ` ${err.key}`, err);
        break;
      case 'subtitle-link-missing-error':
        warnAndNotify('SubtitleLinkMissingError', `Subtitle Link is missing: ${err.key}`, err);
        break;
      case 'stream-missing-error':
        warnAndNotify('StreamMissingError', `Stream link is missing: ${err.key}`, err);
        break;
      case 'missing-link-error':
        warnAndNotify('MissingLinkError', `Link is missing: ${err.kind} ${err.rel}`, err);
        break;
      case 'missing-var-error':
        warnAndNotify(
          'MissingVarError',
          `Unable to load JWPlayer: Missing environment variable ${err.key}`,
          err,
        );
        break;
      case 'jwplayer-load-error':
        warnAndNotify('JWPlayerLoadError', err.key, err);
        break;
      default:
        assertNever(err);
    }
  };

  const initializationSuccess = (options: ReqHlsPlayerOptions, player: JWPlayerFn) => {
    if (!containerRef.current) return;

    const playerObj = player(containerRef.current);
    log(`JWPlayer options for ${id}`, options);
    setup(playerObj, options);

    options.autoLoadIndex.do((index) => playerObj.setCurrentCaptions(index));

    playerObj.on('error', () => handleFallback('error', playerObj, options));
    playerObj.on('setupError', () => handleFallback('setupError', playerObj, options));
  };

  const addSpriteAndCaptions = (options: ReqHlsPlayerOptions): ReqHlsPlayerOptions => {
    const { sprite, subtitles } = options;
    const tracks = just({})
      .assign('sprite', sprite)
      .assign('subtitles', subtitles)
      .map<TracksSourceFile[]>(({ sprite, subtitles }) => [
        thumbnailsSourceFile(sprite),
        ...subtitles,
      ])
      .getOrElseValue([]);
    return {
      ...options,
      tracks: tracks,
      sprite: nothing(),
      subtitles: nothing(),
    };
  };

  const addPreviewImage = (options: ReqHlsPlayerOptions): ReqHlsPlayerOptions => {
    return options.thumbnailImage
      .map<ReqHlsPlayerOptions>((thumb) => ({
        ...options,
        image: thumb,
        thumbnailImage: nothing(),
      }))
      .getOrElseValue(options);
  };

  const checkForSources = (options: ReqHlsPlayerOptions): ReqHlsPlayerOptions => {
    return options.sources
      .andThen((sources) => whenZero(sources.length))
      .map<ReqHlsPlayerOptions>(() => ({ ...options, sources: nothing() }))
      .getOrElseValue(options);
  };

  const finalizeOptions: (options: ReqHlsPlayerOptions) => ReqHlsPlayerOptions = pipe(
    checkForSources,
    addSpriteAndCaptions,
    addPreviewImage,
  );

  return (
    <div
      id={id}
      className={`${className} ${style.root}`}
      ref={containerRef}
      data-test-jwplayer={id}
    />
  );
}

export default observer(JWPlayer);
